/**
 * Sistema Unificado de Filtros - Arquitetura Limpa
 * 
 * Este hook centraliza TODA a lógica de filtragem em duas camadas:
 * 1. Base Filters: Afetam contadores dos cards (nome, status, filtros avançados)
 * 2. Scope Filters: Apenas filtragem visual (cards de escopo)
 * 
 * Fluxo de dados:
 * [Raw Data] -> [Base Filters] -> baseFilteredData (para cards)
 *                     ↓
 *            [Scope Filters] -> fullFilteredData (para tabela)
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DynamicFilter, FilterConfig, FilterOperator } from '@/types/filters'
import { normalizeString } from '@/utils/stringUtils'

// Tipos do sistema
export type ActivityStatusFilter = 'all' | 'active' | 'inactive' | 'away'

export interface CardFilter {
  type: 'status' | 'salary' | 'allocation'
  field: string
  state: 'updated' | 'not_updated'
}

export interface BaseFilters {
  globalFilter: string              // Pesquisa por nome
  activityStatus: ActivityStatusFilter  // Status de atividade
  advancedFilters: DynamicFilter[]  // Filtros avançados dinâmicos
}

export interface ScopeFilters {
  cardFilter: CardFilter | null     // Filtro do card ativo
}

export interface FilterSystemState<T> {
  // Estados dos filtros
  baseFilters: BaseFilters
  scopeFilters: ScopeFilters
  
  // Dados computados
  baseFilteredData: T[]   // Para contadores dos cards
  fullFilteredData: T[]   // Para exibição na tabela
  
  // Estado derivado
  hasActiveFilters: boolean
}

export interface FilterSystemActions {
  // Base Filters
  setGlobalFilter: (value: string) => void
  setActivityStatus: (status: ActivityStatusFilter) => void
  addAdvancedFilter: (columnId: string) => void
  updateAdvancedFilter: (filterId: string, updates: Partial<DynamicFilter>) => void
  removeAdvancedFilter: (filterId: string) => void
  clearAdvancedFilters: () => void
  
  // Scope Filters
  setCardFilter: (filter: CardFilter | null) => void
  clearCardFilter: () => void
  
  // Utilities
  clearAllFilters: () => void
  getActiveFilterChips: () => Array<{
    filterId: string
    label: string
    displayValue: string
    onRemove: () => void
  }>
}

interface UseFilterSystemProps {
  availableFilters: FilterConfig[]
  urlSync?: boolean
  // Mapa de acessores para campos complexos como addresses[0].city
  fieldAccessors?: Record<string, (item: any) => any>
}

export const useFilterSystem = <T extends Record<string, any>>(
  data: T[],
  { availableFilters, urlSync = true, fieldAccessors = {} }: UseFilterSystemProps
) => {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // === ESTADO BASE ===
  const [baseFilters, setBaseFilters] = useState<BaseFilters>(() => ({
    globalFilter: urlSync ? searchParams.get('search') || '' : '',
    activityStatus: urlSync ? (searchParams.get('status') as ActivityStatusFilter) || 'active' : 'active',
    advancedFilters: urlSync && availableFilters.length > 0
      ? loadAdvancedFiltersFromUrl(searchParams, availableFilters)
      : []
  }))
  
  // Carregar filtros da URL apenas uma vez, quando availableFilters estiver estável
  const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(!urlSync || availableFilters.length > 0)
  
  useEffect(() => {
    if (urlSync && !hasLoadedFromUrl && availableFilters.length > 0) {
      const urlFilters = loadAdvancedFiltersFromUrl(searchParams, availableFilters)
      if (urlFilters.length > 0) {
        setBaseFilters(prev => ({ ...prev, advancedFilters: urlFilters }))
      }
      setHasLoadedFromUrl(true)
    }
  }, [urlSync, hasLoadedFromUrl, availableFilters, searchParams])
  
  const [scopeFilters, setScopeFilters] = useState<ScopeFilters>(() => ({
    cardFilter: urlSync ? loadCardFilterFromUrl(searchParams) : null
  }))
  
  // === COMPUTAÇÃO DOS DADOS FILTRADOS ===
  
  // Camada 1: Aplicar filtros base (para contadores dos cards)
  const baseFilteredData = useMemo((): T[] => {
    let filtered = [...data]
    
    // Filtro por nome (globalFilter)
    if (baseFilters.globalFilter.trim()) {
      const searchTerm = normalizeString(baseFilters.globalFilter)
      filtered = filtered.filter(item => 
        normalizeString(item.name || '').includes(searchTerm)
      )
    }
    
    // Filtro por status de atividade
    if (baseFilters.activityStatus !== 'all') {
      const statusValues = getStatusValues(baseFilters.activityStatus)
      filtered = filtered.filter(item => 
        statusValues.includes(item.status)
      )
    }
    
    // Filtros avançados
    baseFilters.advancedFilters.forEach(filter => {
      if (!filter.enabled || !isValidFilterValue(filter.value)) return
      
      filtered = filtered.filter(item => {
        // Usar accessor customizado se disponível, senão usar getNestedValue padrão
        const cellValue = fieldAccessors[filter.columnId] 
          ? fieldAccessors[filter.columnId](item)
          : getNestedValue(item, filter.columnId)
        
        return applyFilterOperator(cellValue, filter.operator, filter.value)
      })
    })
    
    return filtered
  }, [data, baseFilters, fieldAccessors])
  
  // Camada 2: Aplicar filtros de escopo (para exibição final)
  const fullFilteredData = useMemo((): T[] => {
    if (!scopeFilters.cardFilter) return baseFilteredData
    
    return baseFilteredData.filter(item => 
      applyCardFilter(item, scopeFilters.cardFilter)
    )
  }, [baseFilteredData, scopeFilters.cardFilter])
  
  // === ACTIONS PARA BASE FILTERS ===
  
  const setGlobalFilter = useCallback((value: string) => {
    setBaseFilters(prev => ({ ...prev, globalFilter: value }))
  }, [])
  
  const setActivityStatus = useCallback((status: ActivityStatusFilter) => {
    setBaseFilters(prev => ({ ...prev, activityStatus: status }))
  }, [])
  
  const addAdvancedFilter = useCallback((columnId: string) => {
    const config = availableFilters.find(f => f.columnId === columnId)
    if (!config) return
    
    const newFilter: DynamicFilter = {
      id: `adv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      columnId: config.columnId,
      operator: config.operators[0],
      value: getDefaultValueForType(config.type),
      type: config.type,
      label: config.label,
      enabled: true
    }
    
    setBaseFilters(prev => ({
      ...prev,
      advancedFilters: [...prev.advancedFilters, newFilter]
    }))
  }, [availableFilters])
  
  const updateAdvancedFilter = useCallback((filterId: string, updates: Partial<DynamicFilter>) => {
    setBaseFilters(prev => ({
      ...prev,
      advancedFilters: prev.advancedFilters.map(filter => 
        filter.id === filterId ? { ...filter, ...updates } : filter
      )
    }))
  }, [])
  
  const removeAdvancedFilter = useCallback((filterId: string) => {
    setBaseFilters(prev => ({
      ...prev,
      advancedFilters: prev.advancedFilters.filter(filter => filter.id !== filterId)
    }))
  }, [])
  
  const clearAdvancedFilters = useCallback(() => {
    setBaseFilters(prev => ({ ...prev, advancedFilters: [] }))
  }, [])
  
  // === ACTIONS PARA SCOPE FILTERS ===
  
  const setCardFilter = useCallback((filter: CardFilter | null) => {
    setScopeFilters(prev => ({ ...prev, cardFilter: filter }))
  }, [])
  
  const clearCardFilter = useCallback(() => {
    setScopeFilters(prev => ({ ...prev, cardFilter: null }))
  }, [])
  
  // === UTILITIES ===
  
  const clearAllFilters = useCallback(() => {
    setBaseFilters({
      globalFilter: '',
      activityStatus: 'active',
      advancedFilters: []
    })
    setScopeFilters({ cardFilter: null })
  }, [])
  
  const getActiveFilterChips = useCallback(() => {
    const chips: Array<{
      filterId: string
      label: string
      displayValue: string
      onRemove: () => void
    }> = []
    
    // Global filter chip
    if (baseFilters.globalFilter.trim()) {
      chips.push({
        filterId: 'global',
        label: 'Nome',
        displayValue: `contém "${baseFilters.globalFilter}"`,
        onRemove: () => setGlobalFilter('')
      })
    }
    
    // Activity status chip (só se não for o padrão)
    if (baseFilters.activityStatus !== 'active') {
      chips.push({
        filterId: 'activity',
        label: 'Status',
        displayValue: translateActivityStatus(baseFilters.activityStatus),
        onRemove: () => setActivityStatus('active')
      })
    }
    
    // Advanced filters chips
    baseFilters.advancedFilters.forEach(filter => {
      if (filter.enabled && isValidFilterValue(filter.value)) {
        chips.push({
          filterId: filter.id,
          label: filter.label,
          displayValue: formatFilterValue(filter),
          onRemove: () => removeAdvancedFilter(filter.id)
        })
      }
    })
    
    // Card filter chip
    if (scopeFilters.cardFilter) {
      const { type, field, state } = scopeFilters.cardFilter
      const fieldLabel = getCardFieldLabel(type, field)
      let stateLabel = state === 'updated' ? 'Atualizado' : 'Não Atualizado'

      if (type === 'salary') {
        stateLabel = state === 'updated' ? 'Correspondente' : 'Não Correspondente'
      } else if (type === 'allocation') {
        stateLabel = state === 'updated' ? 'Inclui' : 'Exclui'
      }
     
      chips.push({
        filterId: 'cardFilter',
        label: fieldLabel,
        displayValue: stateLabel,
        onRemove: clearCardFilter
      })
    }
    
    return chips
  }, [baseFilters, scopeFilters, setGlobalFilter, setActivityStatus, removeAdvancedFilter, clearCardFilter])
  
  // Estado derivado
  const hasActiveFilters = useMemo(() => {
    return baseFilters.globalFilter.trim() !== '' ||
           baseFilters.activityStatus !== 'active' ||
           baseFilters.advancedFilters.some(f => f.enabled && isValidFilterValue(f.value)) ||
           scopeFilters.cardFilter !== null
  }, [baseFilters, scopeFilters])
  
  // === URL SYNC ===
  useEffect(() => {
    if (!urlSync || !hasLoadedFromUrl) return
    
    const params = new URLSearchParams(searchParams)
    const activeAdvancedFilters = baseFilters.advancedFilters.filter(
      filter => filter.enabled && isValidFilterValue(filter.value)
    )
    
    if (baseFilters.globalFilter.trim()) {
      params.set('search', baseFilters.globalFilter)
    } else {
      params.delete('search')
    }

    if (baseFilters.activityStatus !== 'active') {
      params.set('status', baseFilters.activityStatus)
    } else {
      params.delete('status')
    }
    
    if (activeAdvancedFilters.length > 0) {
      params.set('filters', encodeUrlState(activeAdvancedFilters.map(toUrlAdvancedFilter)))
    } else {
      params.delete('filters')
    }

    if (scopeFilters.cardFilter) {
      params.set('card', encodeUrlState(scopeFilters.cardFilter))
    } else {
      params.delete('card')
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true })
    }
  }, [baseFilters, scopeFilters.cardFilter, urlSync, hasLoadedFromUrl, searchParams, setSearchParams])
  
  const state: FilterSystemState<T> = {
    baseFilters,
    scopeFilters,
    baseFilteredData,
    fullFilteredData,
    hasActiveFilters
  }
  
  const actions: FilterSystemActions = {
    setGlobalFilter,
    setActivityStatus,
    addAdvancedFilter,
    updateAdvancedFilter,
    removeAdvancedFilter,
    clearAdvancedFilters,
    setCardFilter,
    clearCardFilter,
    clearAllFilters,
    getActiveFilterChips
  }
  
  return {
    ...state,
    ...actions,
    // Para compatibilidade com componentes existentes
    advancedFilters: baseFilters.advancedFilters,
    globalFilter: baseFilters.globalFilter,
    activityStatus: baseFilters.activityStatus
  }
}

// === UTILITY FUNCTIONS ===

const getStatusValues = (status: ActivityStatusFilter): string[] => {
  switch (status) {
    case 'active': return ['ACTIVE', 'active', 'ativo']
    case 'inactive': return ['INACTIVE', 'inactive', 'inativo', 'desligado']
    case 'away': return ['AWAY', 'afastado']
    case 'all': return []
    default: return []
  }
}

const translateActivityStatus = (status: ActivityStatusFilter): string => {
  switch (status) {
    case 'active': return 'Ativo'
    case 'inactive': return 'Inativo'
    case 'away': return 'Afastado'
    case 'all': return 'Todos'
    default: return status
  }
}

const getDefaultValueForType = (type: string): any => {
  switch (type) {
    case 'text': return ''
    case 'number': return 0
    case 'select': return null
    case 'multiselect': return []
    case 'date': return null
    case 'boolean': return null
    default: return null
  }
}

const isValidFilterValue = (value: any): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string' && value.trim() === '') return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}

const formatFilterValue = (filter: DynamicFilter): string => {
  const { operator, value } = filter
  
  if (!isValidFilterValue(value)) return ''
  
  switch (operator) {
    case 'contains': return `contém "${value}"`
    case 'equals': return `= "${value}"`
    case 'in': 
      return Array.isArray(value) ? `em [${value.join(', ')}]` : `= ${value}`
    case 'notIn':
      return Array.isArray(value) ? `não é [${value.join(', ')}]` : `≠ ${value}`
    case 'startsWith': return `inicia com "${value}"`
    case 'endsWith': return `termina com "${value}"`
    case 'greaterThan': return `> ${value}`
    case 'lessThan': return `< ${value}`
    case 'greaterThanOrEqual': return `>= ${value}`
    case 'lessThanOrEqual': return `<= ${value}`
    case 'between': 
      return Array.isArray(value) ? `entre ${value[0]} e ${value[1]}` : String(value)
    default: return String(value)
  }
}

const getCardFieldLabel = (type: string, field: string): string => {
  const labels = {
    status: {
      byUser: 'Atualizado por Colaborador',
      byDHO: 'Atualizado pelo DHO',
      byDP: 'Atualizado pelo DP',
      byTech: 'Atualizado pelo Tech'
    },
    salary: {
      over12Months: 'Mais de 12 meses',
      scheduledIncreases: 'Aumentos agendados',
      clt: 'CLT',
      pj: 'PJ'
    },
    allocation: {
      Absence: 'Ausentes',
      Idle: 'Ociosos',
      IdleUpcoming: 'Alocação prevista',
      NeedsReallocation: 'Precisa de realocação',
      Allocated: 'Alocados'
    }
  }
  
  return (labels as any)[type]?.[field] || field
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((value, key) => value?.[key], obj)
}

const applyFilterOperator = (cellValue: any, operator: FilterOperator, filterValue: any): boolean => {
  // Handle null/undefined values
  if (cellValue === null || cellValue === undefined) {
    return operator === 'isEmpty' ? true : operator === 'isNotEmpty' ? false : false
  }
  
  switch (operator) {
    case 'contains':
      return textContains(cellValue, filterValue)
    case 'notContains':
      return !textContains(cellValue, filterValue)
    case 'equals':
      return equals(cellValue, filterValue)
    case 'notEquals':
      return !equals(cellValue, filterValue)
    case 'startsWith':
      return textStartsWith(cellValue, filterValue)
    case 'endsWith':
      return textEndsWith(cellValue, filterValue)
    case 'greaterThan':
      return numberOrDateGreaterThan(cellValue, filterValue)
    case 'lessThan':
      return numberOrDateLessThan(cellValue, filterValue)
    case 'greaterThanOrEqual':
      return numberOrDateGreaterThanOrEqual(cellValue, filterValue)
    case 'lessThanOrEqual':
      return numberOrDateLessThanOrEqual(cellValue, filterValue)
    case 'between':
      return between(cellValue, filterValue)
    case 'notBetween':
      return !between(cellValue, filterValue)
    case 'in':
      return arrayIncludes(filterValue, cellValue)
    case 'notIn':
      return !arrayIncludes(filterValue, cellValue)
    case 'isEmpty':
      return isEmpty(cellValue)
    case 'isNotEmpty':
      return !isEmpty(cellValue)
    default:
      return true
  }
}

// Implementações dos operadores (copiadas de filterFunctions.ts)
const textContains = (cellValue: any, filterValue: string): boolean => {
  if (typeof cellValue !== 'string' || typeof filterValue !== 'string') return false
  return normalizeString(cellValue).includes(normalizeString(filterValue))
}

const textStartsWith = (cellValue: any, filterValue: string): boolean => {
  if (typeof cellValue !== 'string' || typeof filterValue !== 'string') return false
  return normalizeString(cellValue).startsWith(normalizeString(filterValue))
}

const textEndsWith = (cellValue: any, filterValue: string): boolean => {
  if (typeof cellValue !== 'string' || typeof filterValue !== 'string') return false
  return normalizeString(cellValue).endsWith(normalizeString(filterValue))
}

const equals = (cellValue: any, filterValue: any): boolean => {
  const booleanCellValue = parseBoolean(cellValue)
  const booleanFilterValue = parseBoolean(filterValue)

  if (booleanCellValue !== null && booleanFilterValue !== null) {
    return booleanCellValue === booleanFilterValue
  }

  if (typeof cellValue === 'string' && typeof filterValue === 'string') {
    return normalizeString(cellValue) === normalizeString(filterValue)
  }
  return cellValue === filterValue
}

const numberOrDateGreaterThan = (cellValue: any, filterValue: any): boolean => {
  const numA = parseNumberOrDate(cellValue)
  const numB = parseNumberOrDate(filterValue)
  return numA !== null && numB !== null && numA > numB
}

const numberOrDateLessThan = (cellValue: any, filterValue: any): boolean => {
  const numA = parseNumberOrDate(cellValue)
  const numB = parseNumberOrDate(filterValue)
  return numA !== null && numB !== null && numA < numB
}

const numberOrDateGreaterThanOrEqual = (cellValue: any, filterValue: any): boolean => {
  const numA = parseNumberOrDate(cellValue)
  const numB = parseNumberOrDate(filterValue)
  return numA !== null && numB !== null && numA >= numB
}

const numberOrDateLessThanOrEqual = (cellValue: any, filterValue: any): boolean => {
  const numA = parseNumberOrDate(cellValue)
  const numB = parseNumberOrDate(filterValue)
  return numA !== null && numB !== null && numA <= numB
}

const between = (cellValue: any, filterValue: [any, any]): boolean => {
  if (!Array.isArray(filterValue) || filterValue.length !== 2) return false
  const [min, max] = filterValue
  const num = parseNumberOrDate(cellValue)
  const numMin = parseNumberOrDate(min)
  const numMax = parseNumberOrDate(max)
  return num !== null && numMin !== null && numMax !== null && num >= numMin && num <= numMax
}

const arrayIncludes = (filterValue: any, value: any): boolean => {
  const selectedValues = Array.isArray(filterValue) ? filterValue : [filterValue]
  if (selectedValues.length === 0) return false
  if (value === null || value === undefined || value === '') return false

  if (Array.isArray(value)) {
    return value.some(entry => entry !== null && entry !== undefined && selectedValues.includes(entry))
  }

  return selectedValues.includes(value)
}

const isEmpty = (cellValue: any): boolean => {
  if (cellValue === null || cellValue === undefined) return true
  if (typeof cellValue === 'string') return cellValue.trim() === ''
  if (Array.isArray(cellValue)) return cellValue.length === 0
  return false
}

const parseNumberOrDate = (value: any): number | null => {
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'string') {
    const num = parseFloat(value)
    if (!isNaN(num)) return num
    
    const date = new Date(value)
    if (!isNaN(date.getTime())) return date.getTime()
  }
  return null
}

const parseBoolean = (value: any): boolean | null => {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return null

  const normalizedValue = value.trim().toLowerCase()

  if (normalizedValue === 'true') return true
  if (normalizedValue === 'false') return false

  return null
}

// Função para aplicar filtro de cards (baseada na do filterFunctions.ts)
const applyCardFilter = (item: any, cardFilter: CardFilter | null): boolean => {
  if (!cardFilter) return true
  const { type, field, state } = cardFilter
  
  if (type === 'status') {
    const updatedStatus = item.updatedStatus
    const fieldData = updatedStatus?.[field]
    const status = fieldData?.status
    
    const isUpdated = status === 'UPDATED'
    return state === 'updated' ? isUpdated : !isUpdated
  } 
  
  if (type === 'salary') {
    let matches = false
    
    switch (field) {
      case 'over12Months':
        const months = getMonthsSinceLastSalary(item.salaryHistorySalaries || [])
        matches = months >= 12
        break
      case 'scheduledIncreases':
        matches = hasScheduledIncreases(item.salaryHistorySalaries || [])
        break
      case 'clt':
        matches = getLastSalaryType(item.salaryHistorySalaries || []) === 'CLT'
        break
      case 'pj':
        matches = getLastSalaryType(item.salaryHistorySalaries || []) === 'PJ'
        break
      default:
        return true
    }
    
    return state === 'updated' ? matches : !matches
  }

  if (type === 'allocation') {
    const allocationStatus = item.allocationPreview?.status
    const matches = allocationStatus === field
    return state === 'updated' ? matches : !matches
  }
  
  return true
}

// Funções auxiliares para salary filters (copiadas de filterFunctions.ts)
const getMonthsSinceLastSalary = (salaries: any[]) => {
  if (!salaries?.length) return 0
  
  const now = new Date()
  const pastSalaries = salaries.filter(salary => 
    new Date(salary.date) <= now
  )
  
  if (!pastSalaries.length) return 0
  
  const sortedSalaries = [...pastSalaries].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )
  
  const lastSalaryDate = new Date(sortedSalaries[0].date)
  return Math.floor((now.getTime() - lastSalaryDate.getTime()) / (1000 * 60 * 60 * 24 * 30))
}

const hasScheduledIncreases = (salaries: any[]) => {
  if (!salaries?.length) return false
  const currentDate = new Date()
  return salaries.some(salary => new Date(salary.date) > currentDate)
}

const getLastSalaryType = (salaries: any[]) => {
  if (!salaries?.length) return null
  
  const now = new Date()
  const pastSalaries = salaries.filter(salary => 
    new Date(salary.date) <= now
  )
  
  if (!pastSalaries.length) return null
  
  const sortedSalaries = [...pastSalaries].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )
  
  return sortedSalaries[0].salaryType || null
}

type UrlAdvancedFilter = {
  c: string
  o: FilterOperator
  v: any
  t: DynamicFilter['type']
}

const encodeUrlState = (value: unknown): string => {
  const json = JSON.stringify(value)
  const bytes = new TextEncoder().encode(json)
  let binary = ''

  bytes.forEach(byte => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

const decodeUrlState = <T,>(serialized: string): T | null => {
  try {
    const normalized = serialized.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))

    return JSON.parse(new TextDecoder().decode(bytes)) as T
  } catch {
    try {
      return JSON.parse(atob(serialized)) as T
    } catch (error) {
      console.warn('Failed to parse URL state:', error)
      return null
    }
  }
}

const toUrlAdvancedFilter = (filter: DynamicFilter): UrlAdvancedFilter => ({
  c: filter.columnId,
  o: filter.operator,
  v: filter.value,
  t: filter.type,
})

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null

const readUrlAdvancedFilter = (
  value: unknown,
  availableFilters: FilterConfig[],
  index: number
): DynamicFilter | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const rawFilter = value as Record<string, any>
  const columnId = readString(rawFilter.c) ?? readString(rawFilter.columnId)
  if (!columnId) {
    return null
  }

  const config = availableFilters.find(filter => filter.columnId === columnId)
  if (!config) {
    return null
  }

  const rawOperator = readString(rawFilter.o) ?? readString(rawFilter.operator)
  const operator = config.operators.includes(rawOperator as FilterOperator)
    ? rawOperator as FilterOperator
    : config.operators[0]

  return {
    id: readString(rawFilter.id) ?? `adv_url_${index}_${Date.now()}`,
    columnId,
    operator,
    value: 'v' in rawFilter ? rawFilter.v : rawFilter.value,
    type: config.type,
    label: config.label,
    enabled: typeof rawFilter.enabled === 'boolean' ? rawFilter.enabled : true,
  }
}

const loadAdvancedFiltersFromUrl = (
  searchParams: URLSearchParams, 
  availableFilters: FilterConfig[]
): DynamicFilter[] => {
  const serialized = searchParams.get('filters')
  if (!serialized) return []
  
  const filters = decodeUrlState<unknown>(serialized)
  if (!Array.isArray(filters)) {
    return []
  }

  return filters
    .map((filter, index) => readUrlAdvancedFilter(filter, availableFilters, index))
    .filter(Boolean) as DynamicFilter[]
}

const loadCardFilterFromUrl = (searchParams: URLSearchParams): CardFilter | null => {
  const serialized = searchParams.get('card')
  if (!serialized) {
    return null
  }

  const filter = decodeUrlState<Partial<CardFilter>>(serialized)
  if (!filter || typeof filter !== 'object') {
    return null
  }

  if (
    typeof filter.type !== 'string' ||
    typeof filter.field !== 'string' ||
    (filter.state !== 'updated' && filter.state !== 'not_updated')
  ) {
    return null
  }

  return {
    type: filter.type as CardFilter['type'],
    field: filter.field,
    state: filter.state,
  }
}
